import { useCallback } from 'react';

import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';
import { ContactInformationTypeInput } from '@/types/schema';

import { useUpdateClientMutation } from '../graphql/mutations/generated/updateClient';

export default function useClientContactActions({
  clientId,
  onAddSuccess,
  onEditSuccess,
  onDeleteSuccess,
}: {
  clientId: string;
  onAddSuccess?: () => void;
  onEditSuccess?: () => void;
  onDeleteSuccess?: () => void;
}) {
  const [{ fetching }, updateClient] = useUpdateClientMutation();

  const { handleResponse } = useResponse();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const addContact = useCallback(
    async (contact: ContactInformationTypeInput) => {
      const res = await updateClient({
        data: {
          clientId,
          contacts: {
            toAddContacts: [contact],
          },
        },
      });
      handleResponse({
        condition: !!res.data?.updateClient?.client?.id,
        onSuccess: () => {
          onAddSuccess?.();
        },
        onError: () => {
          showDefaultErrorSnackbar();
        },
      });
    },
    [clientId, handleResponse, onAddSuccess, showDefaultErrorSnackbar, updateClient],
  );

  const editContact = useCallback(
    async (contactId: string, contact: ContactInformationTypeInput) => {
      const res = await updateClient({
        data: {
          clientId,
          contacts: {
            toEditContact: {
              ...contact,
              _id: contactId,
            },
          },
        },
      });
      handleResponse({
        condition: !!res.data?.updateClient?.client?.id,
        onSuccess: () => {
          onEditSuccess?.();
        },
        onError: () => {
          showDefaultErrorSnackbar();
        },
      });
    },
    [clientId, handleResponse, onEditSuccess, showDefaultErrorSnackbar, updateClient],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      const res = await updateClient({
        data: {
          clientId,
          contacts: {
            toRemoveContactIds: [contactId],
          },
        },
      });
      handleResponse({
        condition: !!res.data?.updateClient?.client?.id,
        onSuccess: () => {
          onDeleteSuccess?.();
        },
        onError: () => {
          showDefaultErrorSnackbar();
        },
      });
    },
    [clientId, handleResponse, onDeleteSuccess, showDefaultErrorSnackbar, updateClient],
  );

  return {
    fetching,
    addContact,
    editContact,
    deleteContact,
  };
}
